import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { FranchiseApplication, FranchiseApplicationStatus } from '@/types';
import { collection, query, orderBy, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Card } from '@/components/ui/Card';
import {
  Store,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  Eye,
  Filter,
  Search,
  X,
  CalendarCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_CONFIG: Record<FranchiseApplicationStatus, { label: string; color: string; bgColor: string }> = {
  pending: { label: 'Pendiente', color: 'text-accent', bgColor: 'bg-accent/10' },
  reviewing: { label: 'En Revisión', color: 'text-info', bgColor: 'bg-info/10' },
  meeting_scheduled: { label: 'Reunión Agendada', color: 'text-accent', bgColor: 'bg-accent/10' },
  approved: { label: 'Aprobado', color: 'text-success', bgColor: 'bg-success/10' },
  rejected: { label: 'Rechazado', color: 'text-danger', bgColor: 'bg-danger/10' },
};

export default function FranchiseApplicationsPage() {
  const { user, customClaims } = useAuth();
  const [applications, setApplications] = useState<FranchiseApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<FranchiseApplication | null>(null);
  const [statusFilter, setStatusFilter] = useState<FranchiseApplicationStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  // Only super_admin can access this page
  const isSuperAdmin = customClaims?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      loadApplications();
    }
  }, [isSuperAdmin]);

  const loadApplications = async () => {
    try {
      setLoading(true);

      const q = query(
        collection(db, 'franchise_applications'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        ...doc.data(),
        applicationId: doc.id,
      })) as FranchiseApplication[];

      setApplications(data);
    } catch (error) {
      console.error('Error loading franchise applications:', error);
      toast.error('Error al cargar las solicitudes de franquicia');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (applicationId: string, newStatus: FranchiseApplicationStatus) => {
    try {
      const docRef = doc(db, 'franchise_applications', applicationId);
      await updateDoc(docRef, {
        status: newStatus,
        updatedAt: Timestamp.now(),
        reviewedBy: user?.id,
        reviewedAt: Timestamp.now(),
      });

      setApplications(prev =>
        prev.map(app =>
          app.applicationId === applicationId
            ? { ...app, status: newStatus, updatedAt: Timestamp.now() }
            : app
        )
      );

      if (selectedApplication?.applicationId === applicationId) {
        setSelectedApplication(prev => prev ? { ...prev, status: newStatus } : null);
      }

      toast.success(`Estado actualizado a "${STATUS_CONFIG[newStatus].label}"`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar el estado');
    }
  };

  const saveNotes = async (applicationId: string) => {
    try {
      const docRef = doc(db, 'franchise_applications', applicationId);
      await updateDoc(docRef, {
        notes: adminNotes,
        updatedAt: Timestamp.now(),
      });

      setApplications(prev =>
        prev.map(app =>
          app.applicationId === applicationId
            ? { ...app, notes: adminNotes }
            : app
        )
      );

      toast.success('Notas guardadas');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Error al guardar las notas');
    }
  };

  const formatDate = (timestamp: Timestamp) => {
    return timestamp.toDate().toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filter applications
  const filteredApplications = applications.filter(app => {
    if (statusFilter !== 'all' && app.status !== statusFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        app.fullName.toLowerCase().includes(search) ||
        app.email.toLowerCase().includes(search) ||
        app.phone.includes(search) ||
        app.city.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Stats
  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    reviewing: applications.filter(a => a.status === 'reviewing').length,
    meeting_scheduled: applications.filter(a => a.status === 'meeting_scheduled').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
  };

  // Access control
  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <Card className="p-12 text-center">
          <Store className="w-16 h-16 text-ink-subtle mx-auto mb-4" />
          <h2 className="text-xl font-bold text-ink mb-2">
            Acceso Restringido
          </h2>
          <p className="text-ink-muted">
            Solo los super administradores pueden ver las solicitudes de franquicia.
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-2 rounded w-1/3"></div>
          <div className="h-64 bg-surface-2 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-ink flex items-center gap-3">
          <Store className="w-7 h-7 text-success" />
          Solicitudes de Franquicia
        </h1>
        <p className="text-sm text-ink-muted mt-1">
          Gestiona las solicitudes de apertura de nuevas franquicias
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="p-4 text-center">
          <div className="text-2xl font-bold text-ink">{stats.total}</div>
          <div className="text-xs text-ink-subtle">Total</div>
        </Card>
        <Card className="p-4 text-center bg-accent/10 border-accent/30">
          <div className="text-2xl font-bold text-accent">{stats.pending}</div>
          <div className="text-xs text-accent">Pendientes</div>
        </Card>
        <Card className="p-4 text-center bg-info/10 border-info/30">
          <div className="text-2xl font-bold text-info">{stats.reviewing}</div>
          <div className="text-xs text-info">En Revisión</div>
        </Card>
        <Card className="p-4 text-center bg-accent/10 border-accent/30">
          <div className="text-2xl font-bold text-accent">{stats.meeting_scheduled}</div>
          <div className="text-xs text-accent">Reunión</div>
        </Card>
        <Card className="p-4 text-center bg-success/10 border-success/30">
          <div className="text-2xl font-bold text-success">{stats.approved}</div>
          <div className="text-xs text-success">Aprobados</div>
        </Card>
        <Card className="p-4 text-center bg-danger/10 border-danger/30">
          <div className="text-2xl font-bold text-danger">{stats.rejected}</div>
          <div className="text-xs text-danger">Rechazados</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-subtle" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre, email, teléfono o ciudad..."
              className="w-full pl-10 pr-4 py-2 border border-hairline rounded-lg bg-surface text-ink"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-ink-subtle" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as FranchiseApplicationStatus | 'all')}
              className="px-4 py-2 border border-hairline rounded-lg bg-surface text-ink"
            >
              <option value="all">Todos los estados</option>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Applications List */}
      {filteredApplications.length === 0 ? (
        <Card className="p-12 text-center">
          <Store className="w-16 h-16 text-ink-subtle mx-auto mb-4" />
          <p className="text-ink-muted">
            {searchTerm || statusFilter !== 'all'
              ? 'No hay solicitudes con los filtros seleccionados'
              : 'No hay solicitudes de franquicia todavía'}
          </p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredApplications.map((app) => (
            <Card
              key={app.applicationId}
              className="p-4 hover:border-success/30/50 transition-colors cursor-pointer"
              onClick={() => {
                setSelectedApplication(app);
                setAdminNotes(app.notes || '');
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-ink">
                      {app.fullName}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[app.status].bgColor} ${STATUS_CONFIG[app.status].color}`}>
                      {STATUS_CONFIG[app.status].label}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-ink-muted">
                    <div className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {app.email}
                    </div>
                    <div className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {app.phone}
                    </div>
                    <div className="flex items-center gap-1 text-success font-medium">
                      <MapPin className="w-4 h-4" />
                      {app.city}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(app.createdAt)}
                    </div>
                  </div>

                  {app.message && (
                    <p className="mt-2 text-sm text-ink-subtle line-clamp-2">
                      {app.message}
                    </p>
                  )}
                </div>

                <button className="p-2 text-ink-subtle hover:text-success transition">
                  <Eye className="w-5 h-5" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedApplication && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setSelectedApplication(null)}
        >
          <div
            className="bg-surface rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {selectedApplication.fullName}
                  </h2>
                  <p className="text-white/80 text-sm">
                    Solicitud del {formatDate(selectedApplication.createdAt)}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedApplication(null)}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-ink flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Información de Contacto
                </h3>
                <div className="grid gap-3 pl-7">
                  <a
                    href={`mailto:${selectedApplication.email}`}
                    className="flex items-center gap-2 text-info hover:underline"
                  >
                    <Mail className="w-4 h-4" />
                    {selectedApplication.email}
                  </a>
                  <a
                    href={`tel:${selectedApplication.phone}`}
                    className="flex items-center gap-2 text-info hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    {selectedApplication.phone}
                  </a>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-3">
                <h3 className="font-semibold text-ink flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Ubicación Deseada
                </h3>
                <div className="pl-7">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-success/10 text-success rounded-lg font-medium">
                    <MapPin className="w-4 h-4" />
                    {selectedApplication.city}
                  </span>
                </div>
              </div>

              {/* Message */}
              {selectedApplication.message && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-ink flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Mensaje del Solicitante
                  </h3>
                  <p className="pl-7 text-ink-muted whitespace-pre-wrap">
                    {selectedApplication.message}
                  </p>
                </div>
              )}

              {/* Status Update */}
              <div className="space-y-3">
                <h3 className="font-semibold text-ink flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Estado de la Solicitud
                </h3>
                <div className="flex flex-wrap gap-2 pl-7">
                  {(Object.keys(STATUS_CONFIG) as FranchiseApplicationStatus[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => updateStatus(selectedApplication.applicationId, status)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                        selectedApplication.status === status
                          ? `${STATUS_CONFIG[status].bgColor} ${STATUS_CONFIG[status].color} ring-2 ring-offset-2`
                          : 'bg-surface-2 text-ink-muted hover:bg-surface-3'
                      }`}
                    >
                      {status === 'meeting_scheduled' && <CalendarCheck className="w-4 h-4 inline mr-1" />}
                      {status === 'approved' && <CheckCircle className="w-4 h-4 inline mr-1" />}
                      {status === 'rejected' && <XCircle className="w-4 h-4 inline mr-1" />}
                      {STATUS_CONFIG[status].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Admin Notes */}
              <div className="space-y-3">
                <h3 className="font-semibold text-ink flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Notas Internas
                </h3>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Añade notas sobre esta solicitud de franquicia..."
                  rows={3}
                  className="w-full px-4 py-3 border border-hairline rounded-xl bg-surface text-ink focus:ring-2 focus:ring-success focus:border-transparent transition resize-none"
                />
                <button
                  onClick={() => saveNotes(selectedApplication.applicationId)}
                  className="px-4 py-2 bg-surface-2 text-ink-muted rounded-lg hover:bg-surface-3 transition"
                >
                  Guardar Notas
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
